import { computed } from 'vue';
import { useStateStore } from 'composables/useStateStore';

interface LayoutState {
  collapse?: boolean;
}

/**
 * Access layout state from the state store.
 * The Layout component manages this state via useSharedState.
 */
export function useLayout() {
  const stateStore = useStateStore<LayoutState>();

  const getLayoutEntry = () => {
    const entries = Object.entries(stateStore.state);
    const layoutEntry = entries.find(([entry]) => {
      const state = stateStore.state[entry];
      const type = state.type?.toLowerCase() || '';
      return (
        type.includes('layout') || type === 'elayout' || type === 'e-layout' || type === 'Layout'
      );
    });

    return layoutEntry;
  };

  /**
   * Directly mutate the layout collapse state in the store.
   * This bypasses all watchers and reactive chains.
   */
  const setCollapsed = (value: boolean): void => {
    const layoutEntry = getLayoutEntry();
    if (!layoutEntry) {
      console.warn('[useLayout] No layout found in state store');
      return;
    }

    const [key, entry] = layoutEntry;
    if (entry.props.collapse !== undefined) {
      console.log('[useLayout] Setting collapse to:', value);
      // Use setPropValue with proper type casting
      stateStore.setPropValue(key, 'collapse' as keyof LayoutState, value);
    }
  };

  return {
    collapsed: computed(() => {
      const layoutEntry = getLayoutEntry();
      if (!layoutEntry) return false;

      const value = layoutEntry[1].props?.collapse ?? false;
      return value;
    }),

    setCollapsed,

    toggleCollapsed: () => {
      const current = getLayoutEntry()?.[1].props as LayoutState;
      setCollapsed(!(current?.collapse ?? false));
    },

    isReady: computed(() => !!getLayoutEntry())
  };
}
